import React, {useState} from "react";
import {Button, Drawer, Progress} from "antd";
import {IPoll, IPollOptions, IPollResult, IVoters} from "../types/poll.types";
import '../../../styles/component/poll/poll-drawer.scss';
import '../../../styles/component/poll/poll-result.scss'
import {Check, Eye, Mail, ThumbsDown, Users, X} from "lucide-react";
import EmployeeAvatar from "../../employee/avatar/employee-avatar";
import EmptyData from "../../common/empty-data/empty-data";
import RenderNoResult from "./results/render-no-result";
import RenderTieResult from "./results/render-tie-result";
import RenderWinnerResult from "./results/render-winner-result";

interface IPollDrawerProps {
    open: boolean,
    handleDrawerClose: () => void;
    selectedPoll: IPoll | null;
    handleEndPoll: (id: any) => void;
    handleDeclareResult: (id: any) => void;
    handleAdminViewResult: (id: any) => void;
    result?: IPollResult;
    isAdmin: boolean;
}

const PollDrawer = (props: IPollDrawerProps) => {
    const [selectedOption, setSelectedOption] = useState<IPollOptions | null>(null);
    const hadVoterList = props.selectedPoll?.options?.some(
        (option: IPollOptions) => option.voters?.length > 0
    );

    const handleClose = () => {
        props.handleDrawerClose();
        setSelectedOption(null);
    }

    return (
        <Drawer
            open={props.open}
            onClose={handleClose}
            width={'600px'}
            title={
                <>
                    <div>
                        {props.selectedPoll?.title}
                    </div>
                    <div
                        className={'poll-description'}
                        dangerouslySetInnerHTML={{__html:props.selectedPoll?.description??""}}/>
                </>
            }
        >
            {
                props.selectedPoll?.resultDeclared || props.result ? <div>
                        <>
                            {props.result?.totalVotes === 0 && (
                                <RenderNoResult totalVotes={props.result.totalVotes} selectedPoll={props.selectedPoll}
                                                handleDeclareResult={props.handleDeclareResult}/>
                            )}

                            {props.result?.isTie && (
                                <RenderTieResult result={props.result} selectedPoll={props.selectedPoll}
                                                 handleDeclareResult={props.handleDeclareResult}/>
                            )}

                            {!props.result?.isTie && (
                                <RenderWinnerResult result={props.result} selectedPoll={props.selectedPoll}
                                                    handleDeclareResult={props.handleDeclareResult}/>
                            )}
                        </>
                    </div> :
                    <div className={'poll-drawer-container'}>
                        <div className={'top-section'}>
                            <div className={'poll-status'}>
                                <div className={`status ${props.selectedPoll?.anonymous ? "voting" : "polling"}`}>
                                    {props.selectedPoll?.anonymous ? "Vote" : "Poll"}
                                </div>
                                <div
                                    className={`status ${props.selectedPoll?.anonymous ? "anonymous" : "non-anonymous"}`}>
                                    {props.selectedPoll?.anonymous ? "Anonymous" : "Non-Anonymous"}
                                </div>
                                <div
                                    className={`status ${props.selectedPoll?.anonymous ? "results-after" : "results-live"}`}>
                                    {props.selectedPoll?.anonymous ? "Results After Close" : "Results Live"}
                                </div>
                                <div className={`status ${props.selectedPoll?.active ? "active" : "ended"}`}>
                                    {props.selectedPoll?.active ? "Active" : `${props.selectedPoll?.anonymous ? "Voting Ended" : "Poll Ended"}`}
                                </div>
                            </div>
                            <div className={'total-vote-count'}>
                                <Users
                                    className={'poll-user-icon'}/> {props.selectedPoll?.totalVotes ?? props.selectedPoll?.totalVotes} total
                                votes
                            </div>
                        </div>

                        <div className={'options-heading'}>
                            <h6>OPTIONS</h6>
                        </div>

                        <div className={'poll-options-container'}>
                            {props.selectedPoll?.options.map((item: IPollOptions) => (
                                <div key={item.id} className={'poll-options'} onClick={() => setSelectedOption(item)}>
                                    <div className={'option-and-count'}>
                                        <div>
                                            {item.text}
                                        </div>
                                        {
                                            item.voteCount && <div>
                                                {item.voteCount && item.voteCount} {item.voteCount > 1 ? "Votes" : "Vote"}
                                            </div>
                                        }
                                    </div>
                                    {
                                        (!props.selectedPoll?.anonymous || props.selectedPoll.resultDeclared) && (
                                            <Progress
                                                percent={item.votePercentage}
                                                showInfo={item.votePercentage > 0}
                                            />
                                        )
                                    }

                                    {
                                        hadVoterList && <div className="avatar">
                                            {item.voters.slice(0, 2).map((voter) => (
                                                <EmployeeAvatar
                                                    key={voter.officialEmail}
                                                    thumbnailId={voter.profilePhotoThumbnailId}
                                                    name={voter.name}
                                                    size={15}
                                                />
                                            ))}
                                            {item.voters.length > 2 && (
                                                <span>+{item.voters.length - 2} more</span>
                                            )}
                                        </div>
                                    }
                                </div>
                            ))}
                        </div>
                        {
                            hadVoterList &&
                            <div className={'voters-list-container'}>
                                <h5>{selectedOption?.text ? `Voters List for ${selectedOption?.text}` : "Voters List"}</h5>
                                <div className={'voters-list'}>
                                    {
                                        selectedOption ? (
                                            selectedOption.voters.length > 0 ? (
                                                selectedOption.voters.map((voters: IVoters) => (
                                                    <div
                                                        className={'voter-detail'}
                                                        key={voters.officialEmail}
                                                    >
                                                        <div>
                                                            <EmployeeAvatar
                                                                thumbnailId={voters.profilePhotoThumbnailId}
                                                                name={voters.name}
                                                            />
                                                        </div>
                                                        <div className={'voters-name'}>
                                                            <div>
                                                                {voters.name.firstName} {voters.name.middleName} {voters.name.lastName}
                                                            </div>
                                                            <div>
                                                                <Mail
                                                                    className={'voters-email'}/> {voters.officialEmail}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <EmptyData
                                                    icon={<ThumbsDown/>}
                                                    title={` No one voted for ${selectedOption.text}`}
                                                />
                                            )
                                        ) : (
                                            props.selectedPoll?.options.map((item: IPollOptions) => (
                                                <div key={item.id}>
                                                    {item.voters.map((voters: IVoters) => (
                                                        <div
                                                            className={'voter-detail'}
                                                            key={voters.officialEmail}
                                                        >
                                                            <div>
                                                                <EmployeeAvatar
                                                                    thumbnailId={voters.profilePhotoThumbnailId}
                                                                    name={voters.name}
                                                                />
                                                            </div>
                                                            <div className={'voters-name'}>
                                                                <div>
                                                                    {voters.name.firstName} {voters.name.middleName} {voters.name.lastName} voted
                                                                    for <b>{item.text}</b>
                                                                </div>
                                                                <div>
                                                                    <Mail
                                                                        className={'voters-email'}/> {voters.officialEmail}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ))
                                        )
                                    }
                                </div>
                            </div>
                        }

                        {props.isAdmin && <div className={'admin-poll-action'}>
                            <div>
                                {
                                    props.selectedPoll?.active &&
                                    <Button onClick={() => props.handleEndPoll(props.selectedPoll?.id)}
                                            type={"primary"} className={'end-poll-button'}>
                                        <X className={'poll-action-icon'}/> End Poll
                                    </Button>
                                }
                            </div>

                            <div>
                                {
                                    !props.selectedPoll?.active && props.selectedPoll?.anonymous && !props.selectedPoll?.resultDeclared &&
                                    <Button type={"primary"} className={'declare-result-btn'}
                                            onClick={() => props.handleDeclareResult(props.selectedPoll?.id)}>
                                        <Check className={'poll-action-icon'}/> Declare Result
                                    </Button>
                                }
                            </div>
                            <div>
                                {props.isAdmin && !props.selectedPoll?.active && !props.selectedPoll?.resultDeclared &&
                                <Button type={"primary"} className={'declare-result-btn'}
                                        onClick={() => props.handleAdminViewResult(props.selectedPoll?.id)}>
                                    <Eye className={'poll-action-icon'}/> View Result
                                </Button>
                                }
                            </div>
                        </div>}
                    </div>
            }
        </Drawer>
    )
}

export default PollDrawer;
